import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthService, OrganizationService, NotificationService, ChatNotificationManagerService, ForceUpgradeService } from './services';
import { RoutingService } from './services/routing.service';
import { PushNotificationService } from './services/push-notification.service';
import { Platform } from '@ionic/angular';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private authSubscription: Subscription | undefined;
  private routerSubscription: Subscription | undefined;
  private lastRegisteredOrgId: string | undefined;

  constructor(
      public auth: AuthService,
      public orgService: OrganizationService,
      private notificationService: NotificationService,
      private chatNotificationManager: ChatNotificationManagerService,
      private routingService: RoutingService,
      private pushNotificationService: PushNotificationService,
      public forceUpgrade: ForceUpgradeService,
      private platform: Platform,
      private router: Router
  )
  {
    this.initializeApp();
  }

  async ngOnInit() {
    // Request notification permission as soon as the app mounts
    // This allows notifications to work even if user never visits chat
    if (this.notificationService.isSupported() &&
        this.notificationService.getPermissionStatus() === 'default') {
        await this.notificationService.requestPermission();
    }

    // Initialize agent chat notifications immediately if user is already logged in
    if (this.auth.isUserLoggedIn) {
        await this.initializeAgentNotifications();
    }

    // Also subscribe to auth changes to initialize notifications when user logs in
    this.authSubscription = this.auth.currentUserChanged.subscribe(async () => {
        if (this.auth.isUserLoggedIn) {
            await this.initializeAgentNotifications();
        } else {
            // Reset notifications when user logs out
            this.chatNotificationManager.reset();
        }
    });
  }

  ngOnDestroy() {
    if (this.authSubscription) {
        this.authSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
        this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Initialize agent chat notifications
   */
  private async initializeAgentNotifications(): Promise<void> {
    // Add a small delay to ensure organization is loaded
    if (!this.orgService.organization) {
        // Wait for organization with timeout
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds

        await new Promise<void>((resolve) => {
            const interval = setInterval(() => {
                attempts++;
                if (this.orgService.organization || attempts >= maxAttempts) {
                    clearInterval(interval);
                    resolve();
                }
            }, 100);
        });
    }

    // Initialize notifications
    if (this.orgService.organization) {
        await this.chatNotificationManager.initializeAgentNotifications();
    }
  }

  initializeApp(): void {
    this.routingService.initRouting();
    this.platform.ready().then(async () => {
      await this.forceUpgrade.enforceIfNeeded();
      if (this.forceUpgrade.blocking) {
        return;
      }

      // Push registration is anonymous and keyed on the :organizationId route param.
      // Re-register whenever navigation lands on a different org (REQ-5, REQ-6).
      this.routerSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => this.handleOrganizationContext());

      // Cover the initial navigation if it already completed before this subscription.
      this.handleOrganizationContext();
    });
  }

  private handleOrganizationContext(): void {
    const organizationId = this.extractOrganizationId();

    if (!organizationId || organizationId === this.lastRegisteredOrgId) {
      return;
    }

    this.lastRegisteredOrgId = organizationId;
    const customerId = this.auth.customerId || undefined;
    void this.pushNotificationService.initialize(organizationId, customerId);
  }

  /**
   * Walk the activated route tree for the resolved `:organizationId` param.
   */
  private extractOrganizationId(): string | undefined {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (route) {
      if (route.params && route.params['organizationId']) {
        return route.params['organizationId'];
      }
      route = route.firstChild;
    }

    return undefined;
  }

  /**
   * Returns bool to indicate if navigation should use an animation.
   * Animations are only used on small screen mobile devices.
   */
  useAnimatedNavigation(): boolean {
      return window.innerWidth < 600;
  }
}
